import { ErrorResponse } from "@/app/types/error";
import {
  getApi,
  getApiURL,
  patchApi,
  postApi,
} from "@/app/api/config";
import { getItemSessionStorage } from "@/lib/utils";
import { ELocalStorageKeys } from "@/app/types/localStorage";
import { ELocale } from "@/app/types/locale";
import {
  AddInstitutionDTO,
  ProviderDTO,
  ProviderInstitutionDTO,
  UpdateProviderDTO,
  UpdateProviderInstitutionDTO,
} from "./types";

export const getProviderMe = async (): Promise<
  { status: string; data: ProviderDTO } | ErrorResponse
> => {
  const token = await getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await getApi<
    { status: string; data: ProviderDTO } | ErrorResponse
  >("/provider/me", {}, token);

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const updateProviderMe = async (
  data: UpdateProviderDTO
): Promise<{ status: string; data: ProviderDTO } | ErrorResponse> => {
  const token = await getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await patchApi<
    { status: string; data: ProviderDTO } | ErrorResponse
  >("/provider/me", { body: data }, token);

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const updateProviderInstitution = async (
  institutionId: string,
  data: UpdateProviderInstitutionDTO
): Promise<
  { status: string; data: ProviderInstitutionDTO } | ErrorResponse
> => {
  const token = await getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await patchApi<
    { status: string; data: ProviderInstitutionDTO } | ErrorResponse
  >(
    `/provider-institution/${institutionId}`,
    { body: data },
    token
  );

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export const addProviderInstitution = async (
  data: AddInstitutionDTO
): Promise<{ status: string; data: ProviderDTO } | ErrorResponse> => {
  const token = await getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await patchApi<
    { status: string; data: ProviderDTO } | ErrorResponse
  >(
    "/provider-institution/add",
    { body: data },
    token
  );

  if (response.status === "fail") return response as ErrorResponse;
  return response;
};

export interface MealNoteTranslation {
  translatedText: string;
  detectedSourceLanguage?: string;
  targetLanguage?: string;
  translated: boolean;
}

export const translateMealNote = async (
  text: string,
  locale: ELocale
): Promise<
  { status: string; data: MealNoteTranslation } | ErrorResponse
> => {
  const token = await getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await postApi<
    { status: string; data: MealNoteTranslation } | ErrorResponse
  >(
    "/provider/translation/meal",
    {
      body: {
        text,
        locale,
      },
    },
    token
  );

  if (response.status === "fail") return response as ErrorResponse;

  return response as {
    status: string;
    data: MealNoteTranslation;
  };
};

export const getPatientMealPhoto = async (
  patientId: string,
  mealEntryId: string
): Promise<Blob> => {
  const token = getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await fetch(
    getApiURL(
      `/provider/patient/${patientId}/meal-entry/${mealEntryId}/photo`
    ),
    {
      headers: {
        Accept: "image/*",
        Authorization: token ? `Bearer ${token}` : "",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Meal photo unavailable.");
  }

  return response.blob();
};

export const getPatientClinicalRecords = async (
  patientId: string
): Promise<
  | {
      status: string;
      data: import("./types").ClinicalRecordDTO[];
    }
  | ErrorResponse
> => {
  const token = getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await getApi<
    | {
        status: string;
        data: import("./types").ClinicalRecordDTO[];
      }
    | ErrorResponse
  >(
    `/provider/patient/${patientId}/clinical-records`,
    {},
    token
  );

  if (response.status === "fail") return response as ErrorResponse;

  return response as {
    status: string;
    data: import("./types").ClinicalRecordDTO[];
  };
};

export const getPatientClinicalRecordFile = async (
  patientId: string,
  clinicalRecordId: string
): Promise<Blob> => {
  const token = getItemSessionStorage(ELocalStorageKeys.TOKEN);

  const response = await fetch(
    getApiURL(
      `/provider/patient/${patientId}/clinical-records/${clinicalRecordId}/file`
    ),
    {
      headers: {
        Accept: "application/pdf,image/jpeg,image/png",
        Authorization: token ? `Bearer ${token}` : "",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Clinical record unavailable.");
  }

  return response.blob();
};
